import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { default as Form } from '../connectedForm';
import QueryDropdown from '../connectedForm/QueryDropdown';
import * as entities from '../../graphql/entities';
import { DispatchInstructions } from '../../graphql/entities';
import Dropdown from '../connectedForm/Dropdown';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { Button, SuccessButton } from '../connectedForm/Button';
import BlurTextField from '../connectedForm/BlurTextField/BlurTextField';
import PrintDispatchLabels from 'graphql/mutations/sales/PrintDispatchLabels.graphql';
import CheckPopUpBlockerFunction from '../../utils/CheckPopUpBlockerFunction';
import { serviceActions } from '../../state/modules/service';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import { useDispatch } from 'react-redux';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { ModalBottomButtonContainer } from './Styles';
import { BasicHeader } from '../../actions/BasicHeader';
import {
  AlertType,
  IDispatchInstruction,
  IDispatchInstructionInput,
  IDispatchLabelInput,
  ILabelPrintRequestInput,
  LabelFormat,
  Maybe,
  PrinterType,
} from '../../graphql/schema/p4types';
import Cookies from 'universal-cookie';
import FluentModal from './FluentModal';

type ILabelPrintModalProps = {
  data: { [key: string]: Maybe<Array<IDispatchInstruction>> } | Maybe<Array<IDispatchInstruction>>;
  onClose: () => void;
  isOpen: boolean;
};

const DISPATCH_LABEL_SETTINGS = 'dispatchLabelSettings';

const LabelPrintModal = ({ data, onClose, isOpen }: ILabelPrintModalProps) => {
  const index = entities.DispatchInstructions.workspaceUrl?.replace(/^\/|\/$/g, '');
  const dispatchInstructions: Maybe<Array<IDispatchInstruction>> = data?.[index as string] || data;
  const dispatch = useDispatch();

  const cookies = new Cookies();

  const preservedSettings = cookies.get(DISPATCH_LABEL_SETTINGS) || {
    selectedPrinterType: null,
    systemPrinterId: null,
    labelFormat: null,
  };

  const [selectedPrinterType, setSelectedPrinterType] = useState<Maybe<{ value: PrinterType }>>(
    preservedSettings.selectedPrinterType
  );
  const [systemPrinterId, setSystemPrinterId] = useState<Maybe<number>>(preservedSettings.systemPrinterId);
  const [labelFormat, setLabelFormat] = useState<Maybe<{ value: LabelFormat }>>(preservedSettings.labelFormat);

  const [printDispatchLabels] = useMutation(PrintDispatchLabels, {
    onError: (data) =>
      handleComponentQueryError(data, { dispatch, entity: DispatchInstructions, name: 'Could not print labels' }),
  });

  const print = () => {
    if (!dispatchInstructions || dispatchInstructions.length === 0) {
      onClose();
      dispatch(
        serviceActions.genericAlert(
          'Could not determine labels to print',
          'Please use individual selection boxes to choose dispatch instructions for label printing.',
          AlertType.Warning
        )
      );
      return null;
    }

    const labelPrintRequests: Array<ILabelPrintRequestInput> = [];

    cookies.set(
      DISPATCH_LABEL_SETTINGS,
      {
        selectedPrinterType: selectedPrinterType,
        systemPrinterId: systemPrinterId,
        labelFormat: labelFormat,
      },
      { path: '/' }
    );

    for (const dispatchInstruction of dispatchInstructions) {
      labelPrintRequests.push({
        dispatchInstruction: dispatchInstruction as IDispatchInstructionInput,
        printerType: selectedPrinterType?.value,
        systemPrinterId: systemPrinterId,
        labelFormat: labelFormat?.value,
      });
    }

    printDispatchLabels({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          labelPrintRequests: normalizeVariables(labelPrintRequests),
        },
      },
    }).then((response) => {
      let systemPrinterLabelCount = 0;
      for (const dispatchInstruction of response?.data?.printDispatchLabels.result) {
        const labels: Array<IDispatchLabelInput> = [];
        for (const dispatchLabel of dispatchInstruction.dispatchLabels as Array<IDispatchLabelInput>) {
          if (dispatchLabel.labelFormat === labelFormat?.value) {
            labels.push(dispatchLabel);
          }
        }

        // if selected printer is local, open the file in new tab
        if (selectedPrinterType?.value === 'LOCAL') {
          for (const label of labels) {
            const newWindow = CheckPopUpBlockerFunction(
              { dispatch },
              `/api/resources/label/${label.fileName}`,
              '_blank'
            );

            if (newWindow) {
              newWindow.print();
            }
          }
        } else if (labels?.length > 0) {
          systemPrinterLabelCount++;
        } else {
          dispatch(
            serviceActions.genericAlert(
              'Label Print Error',
              `No label available for dispatch instruction ${dispatchInstruction.documentNumber}`,
              AlertType.Warning
            )
          );
        }
      }
      if (systemPrinterLabelCount > 0) {
        dispatch(
          serviceActions.genericAlert(
            'Label Printed',
            `Labels for ${systemPrinterLabelCount} dispatch instructions were sent to a system printer`,
            AlertType.Success
          )
        );
      }
    });
    onClose();
  };

  const uniqueLabelCount = dispatchInstructions
    ? Object.keys(
        dispatchInstructions
          ?.flatMap((dispatchInstruction) => dispatchInstruction.dispatchLabels)
          .map((dispatchLabel) => dispatchLabel?.labelFormat)
          .reduce((labelFormats, _labelFormat: LabelFormat) => {
            const count = labelFormats[_labelFormat] || 0;
            labelFormats[_labelFormat] = count + 1;
            return labelFormats;
          }, {}) || {}
      ).length
    : 1;

  return (
    <FluentModal
      isOpen={isOpen}
      styles={{
        main: {
          minWidth: '400px',
          paddingBottom: '60px',
        },
      }}
    >
      <BasicHeader>Label Print Options</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Form.Fieldset cols={1}>
          <BlurTextField value={uniqueLabelCount} readOnly={true} label="Labels" data-testid={'label-print-count'} />
          <Dropdown
            options={fieldMetaData.PrinterType.enumValues}
            label="Output"
            onChange={setSelectedPrinterType}
            value={selectedPrinterType}
            clearable={false}
          />
          {selectedPrinterType && selectedPrinterType.value === 'SYSTEM' ? (
            <QueryDropdown
              {...entities.SystemPrinter.dropdown}
              label="System Printer"
              onChange={setSystemPrinterId}
              valueOnly={true}
              value={systemPrinterId}
              data-testid={'label-print-system-printer'}
            />
          ) : null}
          <Dropdown
            options={fieldMetaData.LabelFormat.enumValues}
            label="Label Format"
            onChange={setLabelFormat}
            value={labelFormat}
          />
        </Form.Fieldset>
        <ModalBottomButtonContainer>
          <Button onClick={onClose}>Cancel</Button>
          <SuccessButton onClick={print}>Print</SuccessButton>
        </ModalBottomButtonContainer>
      </div>
    </FluentModal>
  );
};

export default LabelPrintModal;
